import { FacilityWasteView } from "generated-graphql/graphql";
import { useMemo } from "react";

import { WasteFacilityContextType, WasteFacilityStateContext } from "./WasteFacilityContext";
import { Filter } from "./WasteFacilityFilters";

/**
 * Provider for surfaces that open the WasteStreamDetailsModal outside the
 * per-facility page — e.g. the tenant-wide Waste Streams grid. Supplies the
 * same context shape the per-facility page provides, bounded to the date
 * range the caller already has active (the Waste Streams page header filter)
 * so the modal's internal date-scoped queries (waste line items,
 * destinations, amount aggregates) scan a narrow window of partitions
 * instead of the full WasteLineUnified history.
 *
 * No URL filter sync: tenant pages don't expose facility-scoped filters in
 * the URL. setWasteFacilityState is a no-op for the same reason.
 */
export const TenantWasteFacilityProvider = ({
  facilityId,
  startDate,
  endDate,
  view = FacilityWasteView.All,
  children,
}: {
  facilityId: string;
  /** ISO YYYY-MM-DD — the active header date range, used to bound queries. */
  startDate?: string;
  endDate?: string;
  /**
   * Which waste-stream set the bounded grids/queries show. Defaults to `All`
   * (the tenant-wide Waste Streams page); the submission stepper passes
   * `Reportable` so its grid mirrors what gets submitted.
   */
  view?: FacilityWasteView;
  children: React.ReactNode;
}) => {
  const value: WasteFacilityContextType = useMemo(() => {
    const filters: Partial<Filter> = {
      startDate,
      endDate,
      view,
    };
    return {
      wasteFacilityState: {
        deferredFilters: filters,
        optimisticFilters: filters,
        isPending: false,
      },
      setWasteFacilityState: () => {},
      effectiveWasteFacilityId: facilityId,
      colocationWasteFacility: null,
    };
  }, [facilityId, startDate, endDate, view]);

  return (
    <WasteFacilityStateContext.Provider value={value}>
      {children}
    </WasteFacilityStateContext.Provider>
  );
};
